import { useEffect, useRef, useState } from 'react';
import MWebcam from 'react-webcam';
import { useImageQuality } from './use-image-quality';
import { sleep } from '../../../../util/util';
import { ChipStatus } from '../../../../shared/ppr-chip-status/ppr-chip-status';

interface UseWebcamOptions {
  webcamRef?: React.RefObject<MWebcam>;
}

// The camera preview component in Candidate Portal is larger than in IB,
// so we scale it down when creating the canvas to keep blurriness checks consistent by using the same canvas size.
const WEBCAM_CANVAS_SCALE_DOWN_RATIO = 0.88;

export const useWebcam = ({ webcamRef }: UseWebcamOptions = {}) => {
  const [selectedCamera, setSelectedCamera] = useState<MediaDeviceInfo>();
  const refSelectedCamera = useRef(selectedCamera);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [cameraStatus, setCameraStatus] = useState<ChipStatus>('not-started');
  const [isCameraCheckDone, setIsCameraCheckDone] = useState(false);
  const [permissionState, setPermissionState] = useState<string>('default');
  const [inCheckingTime, setInCheckingTime] = useState(false);
  const intervalCheckRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const { checkImageQuality } = useImageQuality();

  const [errorMessage, setErrorMessage] = useState<string[]>([]);

  const failTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getCameras = async () => {
    const devices = await navigator.mediaDevices?.enumerateDevices?.() ?? [];

    const videoInputs = devices.filter((device) => device.kind === 'videoinput' && device.deviceId !== '');
    setCameras(videoInputs);

    if (videoInputs.length > 0) {
      const targetVideoInput = videoInputs.find((v) => v.label === refSelectedCamera.current?.label) ?? videoInputs[0];
      setSelectedCamera(targetVideoInput);
    }
  };

  const checkCameraStatus = async () => {
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices?.() ?? [];
      const videoInputs = devices.filter((device) => device.kind === 'videoinput');

      if (videoInputs.length === 0) {
        return 'failed';
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: selectedCamera?.deviceId },
        },
      });

      stream.getTracks().forEach((track) => track.stop());

      return videoInputs.length > 0 ? 'passed' : 'failed';
    } catch (error) {
      console.error(error);
      return 'failed';
    }
  };

  const handleCheckCamera = async () => {
    try {
      if (inCheckingTime) return;
      setInCheckingTime(true);
      setCameraStatus('analyzing');
      await sleep(4000);
      const status = await checkCameraStatus();
      if (status === 'passed') {
        if (!selectedCamera) throw new Error('No selected camera');
        const canvas = await getCanvasFromWebcamRef();
        const cameraCheckResult = await checkImageQuality(canvas);
        setErrorMessage(cameraCheckResult?.errors ?? []);
        setCameraStatus(cameraCheckResult.status);
      }
      setInCheckingTime(false);
      setIsCameraCheckDone(true);
    } catch (error) {
      if (error instanceof Error) { 
        console.error("Error message:", error.message); 
      } else { 
        console.error("Unexpected error:", error); 
      }
    }
  };

  const getCanvasFromWebcamRef = async (): Promise<HTMLCanvasElement> => {
    const base64Image = webcamRef?.current?.getScreenshot();
    if (!base64Image) throw new Error('Unable to capture webcam image');

    return await convertBase64ToCanvas(base64Image);
  };

  const removeIntervalCheck = () => {
    if (intervalCheckRef.current) clearInterval(intervalCheckRef.current)
  }

  const convertBase64ToCanvas = (base64Image: string): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64Image;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width * WEBCAM_CANVAS_SCALE_DOWN_RATIO;
        canvas.height = img.height * WEBCAM_CANVAS_SCALE_DOWN_RATIO;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('No canvas context');
        ctx.drawImage(img, 0, 0);
        resolve(canvas);
      };
     img.onerror = (event: Event | string) => { 
      if (typeof event === "string") { 
        reject(new Error(`Failed to load image: ${event}`)); 
      }
      else { 
        const target = event.target as HTMLImageElement; 
        reject(new Error(`Failed to load image: ${target?.src}`)); 
      }
    };
    });
  };

  useEffect(() => {
    getCameras();
  }, [permissionState]);

  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null;
    let isMounted = true;

    const handlePermissionChange = () => {
      if (permissionStatus && isMounted) {
        setPermissionState(permissionStatus.state);

        if (permissionStatus.state === 'granted') {
          setCameraStatus('not-started');
        } else if (permissionStatus.state === 'denied') {
          setErrorMessage([]);
          setCameraStatus('failed');
          setSelectedCamera(undefined);
        }
      }
    };

    const checkCameraPermission = async () => {
      try {
        permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });

        if (!isMounted) return;

        if (permissionStatus.state === 'denied') {
          setTimeout(() => {
            if (isMounted) setCameraStatus('failed');
          }, 1000);
        }
        setPermissionState(permissionStatus.state);

        permissionStatus.addEventListener('change', handlePermissionChange);
      } catch (error) {
        console.error('Error checking camera permission:', error);
      }
    };

    const handleMediaDevicesChange = async () => {
      if (isMounted) {
        await getCameras();
      }
    };

    checkCameraPermission();

    navigator.mediaDevices.addEventListener('devicechange', handleMediaDevicesChange);

    return () => {
      isMounted = false;
      navigator.mediaDevices.removeEventListener('devicechange', handleMediaDevicesChange);

      if (permissionStatus) {
        permissionStatus.removeEventListener('change', handlePermissionChange);
      }
    };
  }, []);

  useEffect(() => {
    if (failTimeoutRef.current) {
      clearTimeout(failTimeoutRef.current);
      failTimeoutRef.current = null;
    }

    if (!selectedCamera) {
      if (permissionState !== 'default') {
        failTimeoutRef.current = setTimeout(() => {
          setCameraStatus('failed');
        }, 1000);
      }
    } else {
      setCameraStatus('not-started');
      setIsCameraCheckDone(false);
      refSelectedCamera.current = selectedCamera;
    }

    return () => {
      if (failTimeoutRef.current) {
        clearTimeout(failTimeoutRef.current);
        failTimeoutRef.current = null;
      }
    };
  }, [selectedCamera]);

  useEffect(() => {
    let autoCheck = false;
    const interval = setInterval(async () => {
      try {
        const video: boolean | MediaTrackConstraints = refSelectedCamera.current?.deviceId
          ? ({ deviceId: { exact: refSelectedCamera.current.deviceId } } as MediaTrackConstraints)
          : true;

        const stream = await navigator.mediaDevices.getUserMedia({
          video,
        });
        stream.getTracks().forEach((track) => track.stop());

        if (!autoCheck) {
          await getCameras();
          autoCheck = true;
        }
      } catch (error) {
        const resetCameraInfo = () => {
          autoCheck = false;
          setSelectedCamera(undefined);
          setCameras([]);
          setErrorMessage([]);
        };

        if ((error instanceof Error || error instanceof DOMException) && error.message !== 'Permission denied') {
          resetCameraInfo();
          console.error('Failed to access camera', error);
        } else {
          setErrorMessage([]);
          setCameraStatus('failed');
          console.error('Unknown error', error);
        }
      }
    }, 1000);
    intervalCheckRef.current = interval
    return () => clearInterval(intervalCheckRef.current);
  }, []);

  return {
    selectedCamera,
    setSelectedCamera,
    cameras,
    cameraStatus,
    setCameraStatus,
    handleCheckCamera,
    removeIntervalCheck,
    isCameraCheckDone,
    inCheckingTime,
    permissionState,
    errorMessage,
  };
};